import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting } from '@feathr/blackbox';
import { Skeleton, Slider, Text } from '@feathr/components';
import { useOmniSliceTargeting } from '@feathr/extender/components/Targetings';
import type { ListResponse } from '@feathr/rachis';

import useEstimatedReach from './AdWizardAudienceStep.useEstimatedReach';

import * as styles from './AdWizardAudienceStep.css';

export interface IProps {
  campaign: Campaign;
  targetings: ListResponse<Targeting>;
}

const SLIDER_DEFAULT_VALUE = 50;
const SLIDER_MAX_VALUE = 100;
const SLIDER_MIN_VALUE = 10;

function EstimatedReach({ campaign, targetings }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { onChange, selectedPercentage } = useOmniSliceTargeting({
    campaign,
    targetings: targetings.models,
    defaultPercentage: SLIDER_DEFAULT_VALUE,
  });

  const { estimatedReach, isLoading: isLoadingEstimatedReach } = useEstimatedReach({
    campaign,
    targetings,
  });

  // TODO: pull from backend - value of all the available people
  // segments/forecast
  const totalAudienceSize = estimatedReach;

  // The percentage of available people to use for the campaign
  const reach = (totalAudienceSize ?? 0) * (selectedPercentage / 100);
  const formattedReach = numeral(reach).format('0,0');

  const sliderMarks = useMemo(
    () => [
      { value: 10, label: t('Narrow') },
      { value: 20, label: <></> },
      { value: 30, label: <></> },
      { value: 40, label: <></> },
      { value: 50, label: <></> },
      { value: 60, label: <></> },
      { value: 70, label: <></> },
      { value: 80, label: <></> },
      { value: 90, label: <></> },
      { value: 100, label: t('Wide') },
    ],
    [],
  );

  const currentMark = sliderMarks.find((mark) => mark.value === selectedPercentage);

  const label = (
    <>
      {t('Estimated reach:')}{' '}
      {isLoadingEstimatedReach ? (
        <Skeleton disabled={!isLoadingEstimatedReach} width={100} />
      ) : (
        <Text.DefaultSemibold mantineComponent={'span'}>
          {formattedReach} {t('people')}
        </Text.DefaultSemibold>
      )}
    </>
  );

  const helpText = <Text.SmallRegular>{t('Adjust the slider to refine')}</Text.SmallRegular>;

  return (
    <div className={styles.estimatedReach}>
      <Slider
        disabled={isLoadingEstimatedReach}
        helpText={helpText}
        label={label}
        marks={sliderMarks}
        max={SLIDER_MAX_VALUE}
        min={SLIDER_MIN_VALUE}
        onChange={onChange}
        preventLabelOverflow={true}
        restrictToMarks={true}
        tickLabel={null}
        value={currentMark?.value}
      />
    </div>
  );
}

export default observer(EstimatedReach);
